// apps/web/src/hooks/useStopwatch.ts
import * as React from 'react';

export function useStopwatch(opts: { autoStart?: boolean } = {}) {
  const { autoStart = false } = opts;
  const [running, setRunning] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0); // ms
  const startRef = React.useRef<number | null>(null);
  const baseRef = React.useRef(0);
  const intervalRef = React.useRef<number | null>(null);

  const tick = React.useCallback(() => {
    if (startRef.current == null) return;
    const now = Date.now();
    setElapsed(baseRef.current + (now - startRef.current));
  }, []);

  const start = React.useCallback(() => {
    if (intervalRef.current != null) return;
    setRunning(true);
    startRef.current = Date.now();
    intervalRef.current = window.setInterval(tick, 100);
  }, [tick]);

  const stop = React.useCallback(() => {
    if (intervalRef.current == null) return;
    if (startRef.current != null) {
      const now = Date.now();
      baseRef.current += now - startRef.current;
      startRef.current = null;
      setElapsed(baseRef.current);
    }
    window.clearInterval(intervalRef.current);
    intervalRef.current = null;
    setRunning(false);
  }, []);

  const reset = React.useCallback(() => {
    baseRef.current = 0;
    if (intervalRef.current != null) {
      startRef.current = Date.now();
    } else {
      startRef.current = null;
    }
    setElapsed(0);
  }, []);

  const setElapsedMs = React.useCallback((ms: number) => {
    const next = Number.isFinite(ms) && ms > 0 ? ms : 0;
    baseRef.current = next;
    if (intervalRef.current != null) {
      startRef.current = Date.now();
    } else {
      startRef.current = null;
    }
    setElapsed(next);
  }, []);

  React.useEffect(() => {
    if (autoStart) start();
    return () => {
      if (intervalRef.current != null) {
        window.clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { elapsed, running, start, stop, reset, setElapsed: setElapsedMs };
}

// Pretty formatter
export function fmtTime(ms: number) {
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
